import React, { useEffect, useState } from "react"
import { Button, Dropdown, MenuProps, message, Modal, Space } from "antd"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { useActions } from "../../../hooks/useActions"
import { campaignsTypeEnum, placementsEnum } from "../types/types"

import { ApiUnmanage } from "../../../api-wrapper/campaign/ApiCampaignName"
import { ApiStore } from "../../../api-wrapper/advertising/ApiAdvertising"
import { campaignStates, stateByCampaignId } from "../../../types/customers"
import AddCampaignToProject from "../../../components/AddCampaignToProject"
import { IPlacementDataForUpdate } from "../store/types/placements"
import { IChildData } from "../types/childDataTable"
import SelectAudienceModal from "./SelectAudienceModal"
import { ApiAddTagToCampaign, ApiRemoveTagFromCampaign } from "../api/campaignApi"
import { campaignTagsEnum } from "../types/placement"
import { chunk } from "lodash"
import SettingsForAdsServedOffAmazon from "./SettingsForAdsServedOffAmazon"
import { buildFetchSponsoredProductParams } from "../services/sponsoredProductsParams.service"
import { resolveMaxTargetCpc } from "../hooks/helpers/targetAcos.helper"
import {
  ISelectionStats,
  selectAllChangedCampaigns,
} from "../hooks/helpers/placementMetrics.helper"
import { CAMPAIGN_STATES } from "../../../constants/campaign.constants"
import { dataRepresentationEnum } from "../../../types/customers"
import { weekFilterEnum } from "../types/types"
import { ORDER_TYPES } from "../constants"

const APPLY_PROGRESS_KEY = "apply-on-amazon-placements"

enum options {
  "APPLY_BEST_CPC" = "Apply Best CPC",
  "UNMANAGE" = "Unmanage",
  "APPLY_ON_AMAZON" = "Apply On Amazon",
  "PAUSE" = "Pause",
  "ENABLE" = "Enable",
  "ADD_TO_PROJECT" = "Add to project",
  "SELECT_AUDIENCE" = "Select audience",
  "ADD_TEG_RANKING" = "Add ranking tag",
  "REMOVE_TEG_RANKING" = "Remove ranking tag",
}

interface CampaignActionsProps {
  selectedCampaignIds: number[]
  selectCustomFilters: string[]
  setPlacementsDataForUpdate: React.Dispatch<React.SetStateAction<IPlacementDataForUpdate[]>>
  placementDataForUpdate: IPlacementDataForUpdate[]
  minLimitedCPCByCampaignIdMap: Record<string, number>
}

const CampaignActions: React.FC<CampaignActionsProps> = ({
  selectedCampaignIds,
  setPlacementsDataForUpdate,
  placementDataForUpdate,
  minLimitedCPCByCampaignIdMap,
  selectCustomFilters,
}) => {
  const [isAddCampaignToProjectOpen, setIsAddCampaignToProjectOpen] = useState(false)
  const [isSelectAudienceOpen, setIsSelectAudienceOpen] = useState(false)
  const [isApplyOnAmazonLoading, setIsApplyOnAmazonLoading] = useState(false)
  const [isCampaignActionsLoading, setIsCampaignActionsLoading] = useState(false)
  const [applyPendingIds, setApplyPendingIds] = useState<number[] | null>(null)

  const { campaignData } = useTypedSelector((select) => select.sponsoredProductsSKC)
  const { fetchSponsoredProduct, fetchChangeCampaignsState, fetchPlacements } = useActions()
  const { dateRange, isOrder } = useTypedSelector((select) => select.filter)

  const fetchSponsoredProducts = () => {
    fetchSponsoredProduct(
      buildFetchSponsoredProductParams({ dateRange, isOrder, filterIds: selectCustomFilters })
    )
  }

  const handleApplyBestCpc = () => {
    const campaignIdForBestCPC = new Set<number>()
    placementDataForUpdate.forEach((placementDataForUpdate) => {
      placementDataForUpdate.placements.forEach((p) => {
        if (selectedCampaignIds.includes(p.campaignId)) {
          campaignIdForBestCPC.add(p.campaignId)
        }
      })
    })

    const campaignIdsForChanged = [...campaignIdForBestCPC]
    if (campaignIdsForChanged.length === 0) {
      message.warning("Best CPC not applied — no placement data for selected campaigns")
      return
    }

    setPlacementsDataForUpdate((prev) => {
      return prev.map((placement) => {
        if (campaignIdsForChanged.includes(placement.campaignId)) {
          return {
            campaignId: placement.campaignId,
            placements: placement.placements.map((placement) => {
              return {
                ...placement,
                limitedCPC: placement.bestCPC || placement.limitedCPC,
              }
            }),
          }
        }

        return placement
      })
    })
    message.success(`Best CPC applied to ${campaignIdsForChanged.length} campaign(s)`)
  }

  const performUnmanage = () => {
    setIsCampaignActionsLoading(true)
    ApiUnmanage({
      isManage: false,
      campaignIds: selectedCampaignIds,
      type: campaignsTypeEnum.SPONSORED_PRODUCTS,
    })
      .then((status) => {
        if (status >= 200 && status < 300) {
          message.success("Unmanage success")
          fetchSponsoredProducts()
          return
        }

        message.error("Unmanage error")
      })
      .catch((_e) => {
        message.error("Unmanage error")
      })
      .finally(() => {
        setIsCampaignActionsLoading(false)
      })
  }

  const HandleManageCampaign = () => {
    if (selectedCampaignIds.length === 0) {
      message.error("Please select at least one campaign")
      return
    }

    Modal.confirm({
      title: "Unmanage campaigns",
      content: `Unmanage ${selectedCampaignIds.length} campaign(s)?`,
      okText: "Unmanage",
      onOk: performUnmanage,
    })
  }

  // Apply needs computed placements for every loaded ENABLED campaign, not only expanded rows.
  const handleSaveAmazon = async () => {
    const loadedIds = new Set(placementDataForUpdate.map((data) => Number(data.campaignId)))
    const missingIds = campaignData.campaigns
      .filter(
        (campaign) =>
          campaign.campaignStatus === CAMPAIGN_STATES.ENABLED &&
          !loadedIds.has(Number(campaign.campaignId))
      )
      .map((campaign) => campaign.campaignId)

    if (missingIds.length === 0) {
      applyOnAmazon()
      return
    }

    setIsApplyOnAmazonLoading(true)
    let loaded = 0
    const showProgress = () =>
      message.loading({
        key: APPLY_PROGRESS_KEY,
        content: `Loading placements for Apply on Amazon: ${loaded}/${missingIds.length} campaign(s)`,
        duration: 0,
      })
    showProgress()

    let responses: unknown[]
    try {
      responses = await Promise.all(
        chunk(missingIds, 250).map(async (campaignIds) => {
          const response = await fetchPlacements({
            name: dataRepresentationEnum.PLACEMENT,
            type: weekFilterEnum.DAY,
            campaignNames: campaignIds,
            fromDate: dateRange.from,
            toDate: dateRange.to,
            filterIds: [],
            filterType: isOrder ? ORDER_TYPES.ORDERS : ORDER_TYPES.DATE,
          })
          loaded += campaignIds.length
          showProgress()
          return response
        })
      )
    } catch (_e) {
      responses = [null]
    } finally {
      message.destroy(APPLY_PROGRESS_KEY)
    }

    // A failed chunk would silently drop its campaigns: block the whole Apply instead.
    if (responses.some((response) => !response)) {
      setIsApplyOnAmazonLoading(false)
      message.error("Apply on Amazon cancelled: failed to load placements — nothing was sent")
      return
    }

    // The server omits campaigns with no placement rows in the date range; those have nothing
    // to compute, so only the returned ones must be computed before the payload is built.
    const requestedIds = new Set(missingIds.map(Number))
    const returnedIds = new Set<number>()
    responses.forEach((response) =>
      ((response as IChildData).placementSubResult ?? []).forEach((placement) => {
        if (placement && requestedIds.has(Number(placement.campaignId))) {
          returnedIds.add(Number(placement.campaignId))
        }
      })
    )
    setApplyPendingIds([...returnedIds])
  }

  // Submit only once every requested campaign the server returned has computed placements.
  useEffect(() => {
    if (!applyPendingIds) return
    const computedIds = new Set(placementDataForUpdate.map((data) => Number(data.campaignId)))
    if (!applyPendingIds.every((id) => computedIds.has(id))) return

    setApplyPendingIds(null)
    applyOnAmazon()
  }, [applyPendingIds, placementDataForUpdate])

  const applyOnAmazon = () => {
    const { selectedCampaignIds: changedCampaignIds, selectionStats: helperStats } =
      selectAllChangedCampaigns({
        campaignData,
        placementDataForUpdate,
        minLimitedCPCByCampaignIdMap,
      })

    if (changedCampaignIds.length === 0) {
      console.info("[Apply on Amazon] selectionStats", helperStats)
      setIsApplyOnAmazonLoading(false)
      message.info("No enabled campaign has a bid or placement change to apply")
      return
    }

    setIsApplyOnAmazonLoading(true)

    const sendPlacement: any[] = []

    const filterCampaigns = campaignData.campaigns.filter((campaign) =>
      changedCampaignIds.includes(campaign.campaignId)
    )
    const updatedIds: string[] = []
    const campaignsWithoutPlacements = campaignData.campaigns.filter(
      (campaign) =>
        campaign.campaignStatus === CAMPAIGN_STATES.ENABLED &&
        !placementDataForUpdate.some(
          (data) => Number(data.campaignId) === campaign.campaignId && data.placements.length > 0
        )
    )
    const campaignsWithInvalidMaxCpc: string[] = []

    try {
      filterCampaigns.forEach((x) => {
        const placementData = placementDataForUpdate.find(
          (placement) => x.campaignId === Number(placement.campaignId)
        )
        const placements = placementData?.placements || []
        if (updatedIds.includes(String(x.campaignId))) {
          return
        }
        updatedIds.push(String(x.campaignId))

        const maxCpc = resolveMaxTargetCpc(
          x,
          campaignData.maxTargetCPC,
          campaignData.maxTargetCPCByType
        )
        if (!(maxCpc > 0)) {
          campaignsWithInvalidMaxCpc.push(x.campaignName)
          return
        }

        const findPlacement = (type: placementsEnum) =>
          placements.find((placementRow) => placementRow.placement === type)

        const tos = findPlacement(placementsEnum.TOP_OF_SEARCH)
        const ros = findPlacement(placementsEnum.REST_OF_SEARCH)
        const pp = findPlacement(placementsEnum.PRODUCT_PAGE)
        const ab = findPlacement(placementsEnum.AMAZON_BUSINESS)
        const ad = findPlacement(placementsEnum.AUDIENCE)

        sendPlacement.push({
          keywordBid: x.keywordBid || 0,
          campaignName: x.campaignName,
          campaignId: x.campaignId,
          newKeywordBid: minLimitedCPCByCampaignIdMap[String(x.campaignId)],
          TOS_Acos: tos?.targetAcos,
          TOS_Cpc: tos?.targetCPC,
          TOS_modifire: Number(tos?.newBidAdjustment?.toFixed(2)),
          ROS_Acos: ros?.targetAcos,
          ROS_Cpc: ros?.targetCPC,
          PP_Acos: pp?.targetAcos,
          PP_Cpc: pp?.targetCPC,
          PP_modifire: Number(pp?.newBidAdjustment?.toFixed(2)),
          AB_Acos: ab?.targetAcos,
          AB_Cpc: ab?.targetCPC,
          AB_modifire: Number(ab?.newBidAdjustment?.toFixed(2)),
          AD_Acos: ad?.targetAcos,
          AD_Cpc: ad?.targetCPC,
          AD_modifire: Number(ad?.newBidAdjustment?.toFixed(2)),
          TOS_targetCPCAmazon: Number(tos?.limitedCPC?.toFixed(2)) || 0,
          PP_targetCPCAmazon: Number(pp?.limitedCPC?.toFixed(2)) || 0,
          ROS_targetCPCAmazon: Number(ros?.limitedCPC?.toFixed(2)) || 0,
          AB_targetCPCAmazon: Number(ab?.limitedCPC?.toFixed(2)) || 0,
          AD_targetCPCAmazon: Number(ad?.limitedCPC?.toFixed(2)) || 0,
          ROS_modifire: Number(ros?.newBidAdjustment?.toFixed(2)),
          old_TOS_modifire: Number(tos?.placementModifier?.toFixed(2)) || 0,
          old_ROS_modifire: Number(ros?.placementModifier?.toFixed(2)) || 0,
          old_PP_modifire: Number(pp?.placementModifier?.toFixed(2)) || 0,
          old_AB_modifire: Number(ab?.placementModifier?.toFixed(2)) || 0,
          old_AD_modifire: Number(ad?.placementModifier?.toFixed(2)) || 0,
          audienceId: ad?.audienceId || "",
          old_audienceId: ad?.old_audienceId || "",
        })
      })
    } catch (_e) {
      setIsApplyOnAmazonLoading(false)
      message.error("Failed to prepare campaign data")
      return
    }

    if (campaignsWithoutPlacements.length > 0) {
      message.warning(
        `${campaignsWithoutPlacements.length} enabled campaign(s) skipped: no placement data`
      )
    }

    if (campaignsWithInvalidMaxCpc.length > 0) {
      message.error(
        `${campaignsWithInvalidMaxCpc.length} campaign(s) skipped: Max CPC is 0 — set Max CPC in Settings > Other`
      )
    }

    // Max CPC 0 is a hard "do not touch" ceiling (8c0e392d): those campaigns are not eligible.
    const selectionStats: ISelectionStats = {
      enabledLoaded: helperStats.enabledLoaded,
      eligibleChanged: changedCampaignIds.length - campaignsWithInvalidMaxCpc.length,
      selected: new Set(sendPlacement.map((campaign) => campaign.campaignId)).size,
    }
    console.info("[Apply on Amazon] selectionStats", selectionStats)

    if (selectionStats.selected !== selectionStats.eligibleChanged) {
      setIsApplyOnAmazonLoading(false)
      message.error(
        `Apply on Amazon cancelled: payload has ${selectionStats.selected} of ${selectionStats.eligibleChanged} eligible campaign(s) — nothing was sent`
      )
      return
    }

    if (sendPlacement.length === 0) {
      setIsApplyOnAmazonLoading(false)
      return
    }

    const campaignsDataChunk = chunk(sendPlacement, 250)
    Promise.all(
      campaignsDataChunk.map((campaignsData) =>
        handleSaveAmazonChunk(campaignsData, selectionStats)
      )
    )
      .then((result) => {
        const campaignIds = result.flat()
        const isAllCampaignsSaved = campaignIds.length === sendPlacement.length

        if (campaignIds.length > 0) {
          fetchSponsoredProducts()
        }

        if (isAllCampaignsSaved) {
          message.success("Saved successfully")
          return
        }

        if (campaignIds.length > 0) {
          message.error("Save failed: only some of the campaigns were updated")
        } else {
          message.error("Save failed")
        }
      })
      .catch(() => {
        message.error("Save failed")
      })
      .finally(() => {
        setIsApplyOnAmazonLoading(false)
      })
  }

  async function handleSaveAmazonChunk(campaignsData: any[], selectionStats: ISelectionStats) {
    const data = {
      campaignData: campaignsData,
      selectionStats,
    }

    const response = await ApiStore(data)
    if (response === 200) {
      return campaignsData.map((campaign) => {
        return campaign.campaignId
      })
    }

    return []
  }

  function changeStateCampaign(state: campaignStates) {
    const statesByCampaignId: stateByCampaignId[] = selectedCampaignIds.map((campaignId) => {
      return {
        campaignId: campaignId,
        state,
      }
    })
    fetchChangeCampaignsState(statesByCampaignId)
  }

  function addToProject() {
    setIsAddCampaignToProjectOpen(true)
  }

  function selectAudience() {
    setIsSelectAudienceOpen(true)
  }

  function addTegRanking() {
    setIsCampaignActionsLoading(true)
    ApiAddTagToCampaign({
      campaignIds: selectedCampaignIds,
      tag: campaignTagsEnum.RANKING,
    })
      .then((data) => {
        if (data) {
          message.success("Tag ranking added")
          fetchSponsoredProducts()
        } else {
          message.error("Failed to add ranking tag")
        }
      })
      .catch((_e) => {
        message.error("Failed to add ranking tag")
      })
      .finally(() => {
        setIsCampaignActionsLoading(false)
      })
  }

  function removeTegRanking() {
    setIsCampaignActionsLoading(true)
    ApiRemoveTagFromCampaign({
      campaignIds: selectedCampaignIds,
      tag: campaignTagsEnum.RANKING,
    })
      .then((data) => {
        if (data) {
          message.success("Removed tag ranking successfully")
          fetchSponsoredProducts()
        } else {
          message.error("Removed tag ranking error")
        }
      })
      .catch((_e) => {
        message.error("Failed to remove ranking tag")
      })
      .finally(() => {
        setIsCampaignActionsLoading(false)
      })
  }

  const handleMenuClick: MenuProps["onClick"] = (event) => {
    switch (event.key) {
      case options.APPLY_BEST_CPC:
        handleApplyBestCpc()
        break
      case options.UNMANAGE:
        HandleManageCampaign()
        break
      case options.PAUSE:
        changeStateCampaign(campaignStates.PAUSED)
        break
      case options.ENABLE:
        changeStateCampaign(campaignStates.ENABLED)
        break
      case options.ADD_TO_PROJECT:
        addToProject()
        break
      case options.SELECT_AUDIENCE:
        selectAudience()
        break
      case options.ADD_TEG_RANKING:
        addTegRanking()
        break
      case options.REMOVE_TEG_RANKING:
        removeTegRanking()
        break
    }
  }

  const items: MenuProps["items"] = [
    {
      label: options.APPLY_BEST_CPC,
      key: options.APPLY_BEST_CPC,
    },
    {
      label: options.UNMANAGE,
      key: options.UNMANAGE,
    },
    {
      label: options.PAUSE,
      key: options.PAUSE,
    },
    {
      label: options.ENABLE,
      key: options.ENABLE,
    },
    {
      label: options.ADD_TO_PROJECT,
      key: options.ADD_TO_PROJECT,
    },
    {
      label: options.SELECT_AUDIENCE,
      key: options.SELECT_AUDIENCE,
    },
    {
      label: options.ADD_TEG_RANKING,
      key: options.ADD_TEG_RANKING,
    },
    {
      label: options.REMOVE_TEG_RANKING,
      key: options.REMOVE_TEG_RANKING,
    },
  ]

  const menuProps = {
    items,
    onClick: handleMenuClick,
  }

  return (
    <Space>
      <AddCampaignToProject
        campaignIds={selectedCampaignIds}
        isOpen={isAddCampaignToProjectOpen}
        setIsOpen={setIsAddCampaignToProjectOpen}
      />
      <SelectAudienceModal
        selectedCampaignIds={selectedCampaignIds}
        isOpen={isSelectAudienceOpen}
        setIsOpen={setIsSelectAudienceOpen}
        setPlacementsDataForUpdate={setPlacementsDataForUpdate}
      />
      <Space>
        <Button
          onClick={handleSaveAmazon}
          loading={isApplyOnAmazonLoading}
          disabled={isApplyOnAmazonLoading}
        >
          {options.APPLY_ON_AMAZON}
        </Button>
        <Dropdown.Button menu={menuProps} loading={isCampaignActionsLoading}>
          Campaign Actions
        </Dropdown.Button>
        <SettingsForAdsServedOffAmazon selectedCampaignIds={selectedCampaignIds} />
      </Space>
    </Space>
  )
}

export default CampaignActions
